import { z } from 'zod';
import { vknGecerliMi } from '../validation/vkn.js';

export const paraBirimiSchema = z.enum(['TRY', 'EUR', 'USD', 'GBP']);
export type ParaBirimi = z.infer<typeof paraBirimiSchema>;

/** Pansiyon tipi: Oda / Oda-Kahvalti / Yarim / Tam / Hersey Dahil */
export const pansiyonSchema = z.enum(['OB', 'BB', 'HB', 'FB', 'AI']);
export type Pansiyon = z.infer<typeof pansiyonSchema>;

export const rezervasyonKaynagiSchema = z.enum([
  'DIREKT',
  'TELEFON',
  'WALK_IN',
  'ACENTA',
  'WEB',
  'KANAL', // OTA (channel manager)
  'GRUP',
]);
export type RezervasyonKaynagi = z.infer<typeof rezervasyonKaynagiSchema>;

export const rezervasyonDurumuSchema = z.enum([
  'OPSIYON', // gecici tutma, release suresi var
  'ONAYLI',
  'CHECKED_IN',
  'CHECKED_OUT',
  'IPTAL',
  'NO_SHOW',
]);
export type RezervasyonDurumu = z.infer<typeof rezervasyonDurumuSchema>;

export const odaDurumuSchema = z.enum(['BOS', 'DOLU', 'ARIZALI', 'BLOKE']);
export const temizlikDurumuSchema = z.enum(['TEMIZ', 'KIRLI', 'TEMIZLENIYOR', 'KONTROL_BEKLIYOR']);
export type TemizlikDurumu = z.infer<typeof temizlikDurumuSchema>;

export const isoTarih = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarih YYYY-AA-GG biçiminde olmalı');

export const misafirGirdiSchema = z.object({
  ad: z.string().min(1),
  soyad: z.string().min(1),
  tckn: z.string().nullable().default(null),
  pasaportNo: z.string().nullable().default(null),
  uyruk: z.string().length(3).default('TUR'),
  dogumTarihi: isoTarih.nullable().default(null),
  anneAdi: z.string().nullable().default(null),
  babaAdi: z.string().nullable().default(null),
  dogumYeri: z.string().nullable().default(null),
  cinsiyet: z.enum(['E', 'K', 'BILINMIYOR']).default('BILINMIYOR'),
  belgeSeriNo: z.string().nullable().default(null),
  telefon: z.string().nullable().default(null),
  eposta: z.string().email().nullable().default(null),
  anaMisafir: z.boolean().default(false),
});
export type MisafirGirdi = z.infer<typeof misafirGirdiSchema>;

/**
 * Kurumsal fatura bilgisi.
 *
 * Rezervasyon aninda alinir: sirket adina gelen misafirin faturasi cikista
 * degil, kayit acilirken netlesir. Unvan ve VKN zorunlu; VKN'nin kontrol
 * hanesi de dogrulanir, cunku yanlis VKN faturayi GIB'den geri dondurur.
 */
export const kurumsalFaturaSchema = z.object({
  unvan: z.string().trim().min(2, 'Firma unvanı gerekli').max(200),
  vergiNo: z
    .string()
    .trim()
    .refine((v) => vknGecerliMi(v), 'Vergi kimlik numarası geçersiz'),
  vergiDairesi: z.string().trim().max(100).nullable().default(null),
  eposta: z.string().trim().email('Geçerli bir e-posta girin').nullable().default(null),
  adres: z.string().trim().max(500).nullable().default(null),
});
export type KurumsalFatura = z.infer<typeof kurumsalFaturaSchema>;

export const rezervasyonOlusturSchema = z
  .object({
    girisTarihi: isoTarih,
    cikisTarihi: isoTarih,
    roomTypeId: z.string(),
    roomId: z.string().nullable().default(null),
    yetiskin: z.number().int().min(1).max(10),
    cocuk: z.number().int().min(0).max(10).default(0),
    cocukYaslari: z.array(z.number().int().min(0).max(17)).default([]),
    pansiyon: pansiyonSchema.default('BB'),
    kaynak: rezervasyonKaynagiSchema.default('DIREKT'),
    agencyId: z.string().nullable().default(null),
    ratePlanId: z.string().nullable().default(null),
    toplamTutar: z.number().nonnegative().nullable().default(null),
    paraBirimi: paraBirimiSchema.default('TRY'),
    promosyonKodu: z.string().nullable().default(null),
    not: z.string().max(2000).nullable().default(null),
    misafirler: z.array(misafirGirdiSchema).min(1),
  })
  .refine((r) => r.cikisTarihi > r.girisTarihi, {
    message: 'Çıkış tarihi giriş tarihinden sonra olmalı',
    path: ['cikisTarihi'],
  })
  .refine((r) => r.cocukYaslari.length === 0 || r.cocukYaslari.length === r.cocuk, {
    message: 'Çocuk sayısı ile girilen yaş sayısı uyuşmuyor',
    path: ['cocukYaslari'],
  });
export type RezervasyonOlustur = z.infer<typeof rezervasyonOlusturSchema>;

export const musaitlikSorguSchema = z.object({
  girisTarihi: isoTarih,
  cikisTarihi: isoTarih,
  yetiskin: z.coerce.number().int().min(1).default(2),
  cocuk: z.coerce.number().int().min(0).default(0),
  roomTypeId: z.string().optional(),
  paraBirimi: paraBirimiSchema.default('TRY'),
});
export type MusaitlikSorgu = z.infer<typeof musaitlikSorguSchema>;

export const musaitlikSonucSchema = z.object({
  roomTypeId: z.string(),
  ad: z.string(),
  kalanOda: z.number().int(),
  /** Acentaya ozel kontenjandan kalan (allotment). null = serbest satis */
  kalanKontenjan: z.number().int().nullable(),
  gecelikFiyatlar: z.array(z.object({ tarih: isoTarih, tutar: z.number(), pansiyon: pansiyonSchema })),
  toplamTutar: z.number(),
  paraBirimi: paraBirimiSchema,
});
export type MusaitlikSonuc = z.infer<typeof musaitlikSonucSchema>;
